<script setup lang="ts">
import { computed } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import type { ActivityDto, MaterialDto, ProductDto, StageKey } from '@/stores/buildingBlock'
import { TextField, ChoiceField } from '@haskoning/vue-component-library'
import TransportSelector from '@/components/building-block-form/TransportSelector.vue'
import { useActivityStore } from '@/stores/activity'
import { useConfirmDeleteStore } from '@/stores/confirmDelete'
import { useBuildingBlockStatus } from '@/components/building-block-form/composable/useBuildingBlockStatus'
import { useMaterialImpact } from '@/components/building-block-form/utils/useMaterialImpact'
import { DEFAULT_ACTIVITY_VALUE } from '@/components/building-block-form/constants/buildingBlockDefaults'
import { DEFAULT_DEBOUNCE_DELAY } from '@/utils/debounce'
import { handleWheelOnNumericField } from '@/utils/handleWheelOnNumericField'
import {
  TRANSPORT_ACTIVITY_STAGES,
  PRODUCTION_COMBINED_STAGE,
  PRODUCTION_INDIVIDUAL_STAGES
} from '@/constants/lifecyclePhases'

const props = defineProps<{
  activity: ActivityDto
  material: MaterialDto
  isEquipmentBlock?: boolean
  readonly?: boolean
  product: ProductDto | null
  usedStages: (StageKey | null)[]
  isFirstForStage: boolean
  sameStageCount: number
  onStageChanged: (activity: ActivityDto, newStage: StageKey | null) => void
  onModeChanged: (activity: ActivityDto) => void
}>()
const activityStore = useActivityStore()
const confirmDeleteStore = useConfirmDeleteStore()
const { run } = useBuildingBlockStatus()
const { applyActivityImpact, refreshMaterialImpact } = useMaterialImpact()

const availableStageChoices = computed(() => {
  if (!activityStore.fields?.stage?.choices) return []

  const otherStages = props.usedStages.filter((s) => s !== props.activity.stage)
  const hasIndividual = otherStages.some((s) => s && PRODUCTION_INDIVIDUAL_STAGES.includes(s))
  const hasCombined = otherStages.includes(PRODUCTION_COMBINED_STAGE)

  return activityStore.fields.stage.choices.filter((choice: { value: string }) => {
    if (hasIndividual && choice.value === PRODUCTION_COMBINED_STAGE) return false
    if (hasCombined && PRODUCTION_INDIVIDUAL_STAGES.includes(choice.value)) return false
    return true
  })
})

const debouncedStoreUpdateDynamic = useDebounceFn(async (nextValue?: number | null) => {
  const payload = nextValue === undefined ? props.activity : { ...props.activity, value: nextValue }
  const updatedActivity = await run(() => activityStore.update(payload), props.material.block)
  applyActivityImpact(updatedActivity)
}, DEFAULT_DEBOUNCE_DELAY)

const ondeleteActivity = async () => {
  if (props.readonly) return
  const confirmed = await confirmDeleteStore.confirm({
    message: 'Delete this activity?',
    name: props.activity?.stage ?? ''
  })
  if (!confirmed) return

  await activityStore.delete(props.activity)
  await refreshMaterialImpact(props.material?.id)
}

const onValueTyping = (event: Event) => {
  if (props.readonly) return
  const inputElement = event.target instanceof HTMLInputElement ? event.target : null
  const textValue = inputElement?.value?.trim() ?? ''
  if (textValue === '') {
    debouncedStoreUpdateDynamic(null)
  } else {
    const parsedValue = Number(textValue)
    if (!Number.isNaN(parsedValue)) {
      debouncedStoreUpdateDynamic(parsedValue)
    }
  }
}

const formattedImpact = computed(() =>
  props.activity.impact != null ? props.activity.impact.toFixed(2) : ''
)
</script>

<template>
  <div class="activity-editor px-5">
    <div class="d-flex align-center">
      <span class="text-subtitle-2">
        {{ props.sameStageCount > 1 ? 'Activities – Impact Cost' : 'Activity – Impact Cost' }}: €
        {{ formattedImpact }}
      </span>
      <v-spacer />
      <v-btn
        icon="mdi-delete"
        variant="text"
        size="small"
        :disabled="props.readonly"
        @click="ondeleteActivity"
      />
    </div>
    <v-row>
      <v-col cols="12" sm="6" md="4" lg="4" class="pa-2">
        <!-- Stage -->
        <ChoiceField
          v-if="activityStore.fields?.stage"
          :field="{ ...activityStore.fields.stage, choices: availableStageChoices }"
          :model-value="props.activity"
          :readonly="props.readonly"
          @update="() => props.onStageChanged(props.activity, props.activity.stage)"
          hide-details
        />
      </v-col>
      <!-- Mode -->
      <v-col cols="12" sm="6" md="3" class="pa-2">
        <ChoiceField
          v-if="activityStore.fields?.mode"
          :field="activityStore.fields.mode"
          :model-value="props.activity"
          :no-help-text="true"
          :disabled="!props.isFirstForStage || props.readonly"
          :readonly="props.readonly"
          hide-details
          @update="
            () => {
              props.onModeChanged(props.activity)
            }
          "
        />
      </v-col>
      <v-spacer />
      <v-col cols="12" sm="9" md="3" class="pa-2">
        <v-text-field
          v-if="props.material"
          label="Default value from database"
          variant="outlined"
          :model-value="
            props.product && props.activity.stage
              ? '€ ' + props.product[props.activity.stage]?.toFixed(2)
              : ''
          "
          color="primary"
          hide-details
          readonly
        />
      </v-col>
    </v-row>
    <v-row
      class="mt-2"
      v-if="props.activity.stage && TRANSPORT_ACTIVITY_STAGES.includes(props.activity.stage)"
    >
      <!-- Transport selector -->
      <TransportSelector
        :activity="props.activity"
        :material="props.material"
        :readonly="props.readonly"
        :default_value="
          props.product
            ? props.product[props.activity.stage] || DEFAULT_ACTIVITY_VALUE
            : DEFAULT_ACTIVITY_VALUE
        "
        @update="(value) => value !== undefined && debouncedStoreUpdateDynamic(value)"
      />
    </v-row>
    <!-- normal selector -->
    <v-row v-else>
      <v-col class="pa-0 px-2 pb-6" cols="12" sm="9" md="3">
        <TextField
          v-if="activityStore.fields?.value"
          :field="activityStore.fields?.value"
          :model-value="props.activity"
          :no-help-text="true"
          :readonly="props.readonly"
          hide-details
          @wheel="handleWheelOnNumericField"
          @input="onValueTyping"
        />
      </v-col>
    </v-row>
  </div>
</template>
<style scoped>
.activity-editor {
  /* background-color: #d4dce1; */
  border-radius: 10px;
  border: 1px solid #c0c0c0;
}
</style>
